import { jsxs, jsx } from 'react/jsx-runtime';
import { useReferenceSafeObject, isNil } from '@flex-design-system/fx-utils';
import { styled } from '@flex-design-system/stitches-react';
import { Primitive } from '@radix-ui/react-primitive';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import classNames from 'classnames';
import { forwardRef, useMemo } from 'react';
import { LoadingIndicator } from '../shared/components/loading-indicator/LoadingIndicator.mjs';
import { getButtonThemeColor } from '../utils/color/getButtonThemeColor.mjs';
import { getDisabledDataAttribute } from '../utils/data-attribute/getDisabledDataAttribute.mjs';
import { getGradientColorDataAttribute } from '../utils/data-attribute/getGradientColorDataAttribute.mjs';
import { button, buttonClickable, buttonFocusVisible, buttonBackground, buttonTextColor, buttonContent, buttonInnerContent } from './button.vanilla.mjs';
import { ButtonSlot } from './components/slot/Slot.mjs';

const ButtonImpl = forwardRef(
  ({
    align,
    children,
    className: classNameFromProps,
    color = "primary",
    danger,
    disabled,
    fullWidth,
    loading,
    size,
    style: styleFromProps,
    variant = "solid",
    leftSlot,
    rightSlot,
    ...props
  }, forwardedRef) => {
    const className = classNames(
      button({ fullWidth, size, variant }),
      buttonClickable[variant],
      buttonFocusVisible,
      classNameFromProps
    );
    const style = useReferenceSafeObject(styleFromProps);
    const styles = useMemo(() => {
      const { background, color: textColor } = getButtonThemeColor({
        color,
        danger,
        variant
      });
      return Object.assign(
        {},
        assignInlineVars({
          [buttonTextColor]: textColor !== void 0 ? textColor : null,
          [buttonBackground]: background !== void 0 ? background : null
        }),
        style
      );
    }, [color, danger, style, variant]);
    const isDisabled = (disabled || loading) ?? false;
    const content = props.asChild && isNil(leftSlot) && isNil(rightSlot) ? children : /* @__PURE__ */ jsxs("div", { "data-scope": "button", "data-part": "content", className: buttonContent, children: [
      /* @__PURE__ */ jsxs(
        "div",
        {
          "data-scope": "button",
          "data-part": "inner-content",
          className: buttonInnerContent({ align }),
          children: [
            loading ? /* @__PURE__ */ jsx(
              LoadingIndicator,
              {
                color,
                danger,
                size,
                variant
              }
            ) : /* @__PURE__ */ jsx(ButtonSlot, { children: leftSlot }),
            children
          ]
        }
      ),
      /* @__PURE__ */ jsx(ButtonSlot, { children: rightSlot })
    ] });
    return /* @__PURE__ */ jsx(
      Primitive.button,
      {
        type: "button",
        "aria-disabled": isDisabled,
        "data-scope": "button",
        "data-part": "root",
        className,
        disabled: isDisabled,
        ...getDisabledDataAttribute(isDisabled),
        ...getGradientColorDataAttribute(color),
        style: styles,
        ref: forwardedRef,
        ...props,
        children: content
      }
    );
  }
);
const Button = Object.assign({}, styled(ButtonImpl), {
  /** @deprecated `Button.Slot`을 사용해 주세요. */
  LeftSlotRoot: styled(ButtonSlot),
  /** @deprecated `Button.Slot`을 사용해 주세요. */
  RightSlotRoot: styled(ButtonSlot),
  Slot: styled(ButtonSlot)
});

export { Button };
//# sourceMappingURL=Button.mjs.map
